/**
 * HTTP Service for PerfectServe applications
 * Handles authenticated API requests with consistent headers, error handling, and environment integration
 */
export class HttpService {
    constructor(config) {
        this.config = config;
    }
    /**
     * Update service configuration
     */
    configure(config) {
        this.config = { ...this.config, ...config };
    }
    /**
     * Update auth token
     */
    setAuthToken(token) {
        // Update the token getter function
        this.config.getAuthToken = () => token;
    }
    /**
     * Generate correlation ID for request tracking
     */
    generateCorrelationId() {
        return "ps-web-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now().toString(36);
    }
    /**
     * Resolve full URL with base URL handling
     */
    resolveUrl(url, baseURL) {
        // If URL is already absolute, return as-is
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        // Use provided baseURL or default from config
        const resolvedBaseURL = baseURL || this.config.envConfig.cspApi;
        if (!resolvedBaseURL) {
            throw new Error("HTTP Service: baseURL must be provided either in request config or env config (cspApi). Services should explicitly specify their API base URL.");
        }
        // Ensure base URL doesn't end with slash and URL starts with slash
        const cleanBase = resolvedBaseURL.replace(/\/$/, "");
        const cleanUrl = url.startsWith("/") ? url : "/" + url;
        return cleanBase + cleanUrl;
    }
    /**
     * Build standard headers for all requests
     */
    buildHeaders(customHeaders = {}) {
        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-perfectserve-CorrelationId": this.generateCorrelationId(),
            // Allow overriding ApplicationInfo via defaultHeaders or custom headers
            "x-perfectserve-ApplicationInfo": this.config?.defaultHeaders?.["x-perfectserve-ApplicationInfo"] || "PS-Web/3.x",
            ...this.config.defaultHeaders,
            ...customHeaders,
        };
        // Add auth header if token is available
        const authToken = this.config.getAuthToken();
        if (authToken && !headers["Authorization"]) {
            headers["Authorization"] = `Bearer ${authToken}`;
        }
        return headers;
    }
    /**
     * Handle HTTP errors, especially 401 unauthorized
     */
    handleHttpError(response, url, skipAutoLogout = false) {
        const error = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            status: response.status,
            statusText: response.statusText,
            url: url,
            response: response,
        };
        if (response.status === 401 && !skipAutoLogout) {
            // Call the auth error handler if provided
            if (this.config.onAuthError) {
                this.config.onAuthError(error);
            }
        }
    }
    /**
     * Core fetch wrapper with interceptor logic
     */
    async fetchWithInterceptor(url, options = {}) {
        const { baseURL, timeout = this.config.defaultTimeout || 30000, skipAutoLogout = false, params, responseType, ...fetchOptions } = options;
        // Resolve full URL
        let fullUrl = this.resolveUrl(url, baseURL);
        if (params && Object.keys(params).length > 0) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null)
                    return;
                usp.append(key, String(value));
            });
            const joiner = fullUrl.includes("?") ? "&" : "?";
            fullUrl += usp.toString() ? joiner + usp.toString() : "";
        }
        // Build headers
        const headers = this.buildHeaders(fetchOptions.headers);
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(fullUrl, {
                ...fetchOptions,
                headers,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            // Attempt to parse body for both error and success cases
            const contentType = response.headers.get("content-type") || "";
            const wantsJson = responseType ? responseType === "json" : contentType.includes("application/json");
            const parseBody = async () => {
                try {
                    if (responseType === "blob")
                        return await response.blob();
                    if (responseType === "text")
                        return await response.text();
                    if (wantsJson)
                        return await response.json();
                    // default fallback to text
                    return await response.text();
                }
                catch {
                    return null;
                }
            };
            const parsedBody = await parseBody();
            // Handle HTTP errors
            if (!response.ok) {
                const requestId = response.headers.get("x-request-id") || response.headers.get("x-correlation-id") || null;
                const error = {
                    message: `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status,
                    statusText: response.statusText,
                    url: fullUrl,
                    response,
                    headers: response.headers,
                    body: parsedBody,
                    requestId,
                };
                if (response.status === 401 && !skipAutoLogout && this.config.onAuthError) {
                    this.config.onAuthError(error);
                }
                throw error;
            }
            // Success
            const data = parsedBody;
            return {
                data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
            };
        }
        catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === "AbortError") {
                const timeoutError = {
                    message: `Request timeout after ${timeout}ms`,
                    status: 0,
                    statusText: "Timeout",
                    url,
                };
                throw timeoutError;
            }
            throw error;
        }
    }
    /**
     * HTTP GET method
     */
    async get(url, config = {}) {
        return this.fetchWithInterceptor(url, {
            method: "GET",
            ...config,
        });
    }
    /**
     * HTTP POST method
     */
    async post(url, data, config = {}) {
        return this.fetchWithInterceptor(url, {
            method: "POST",
            body: data ? JSON.stringify(data) : null,
            ...config,
        });
    }
    /**
     * HTTP PUT method
     */
    async put(url, data, config = {}) {
        return this.fetchWithInterceptor(url, {
            method: "PUT",
            body: data ? JSON.stringify(data) : null,
            ...config,
        });
    }
    /**
     * HTTP DELETE method
     */
    async delete(url, config = {}) {
        return this.fetchWithInterceptor(url, {
            method: "DELETE",
            ...config,
        });
    }
    /**
     * HTTP PATCH method
     */
    async patch(url, data, config = {}) {
        return this.fetchWithInterceptor(url, {
            method: "PATCH",
            body: data ? JSON.stringify(data) : null,
            ...config,
        });
    }
}
